/**
 * Order Forms — Platform-Admin authoring surface for a customer's signed commercial
 * order forms and the deployments they entitle.
 *
 * A customer may sign several order forms over the life of a contract (#1, #2 … #N).
 * Each is cumulative: a new order form ADDS incremental deployment lines; it never
 * disturbs deployments already provisioned from earlier forms. Platform Admins author
 * the forms here; the lines marked as deployments then appear in Cloud Operations →
 * Configure for an operator to provision (the shared OrderLineFulfillmentService does
 * the provisioning, taking the contracted infra straight from the line).
 *
 * Reuses the existing /api/v1/admin/orders client (services/api.js) and the shared v2
 * design system — no new backend surface, no bespoke widgets.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Plus, FileText, Building2, Rocket } from 'lucide-react';
import * as api from '../../services/api';
import {
  V2PageShell, Card, Button, Pill, FieldGroup, Input, Select, Label,
  EmptyState, CustomerPicker, pushGlobalToast,
} from '../../components/v2';

// ── Canonical vocabularies — mirror the server-side validators so the form can
//    only ever offer values the OrderService will accept. Labels are end-user text;
//    the value is the wire key the backend validates.
const EDITIONS = [
  { value: 'GOVERNED_WORKFLOW',  label: 'Protected Workflow' },
  { value: 'AGENTIC_PACK',       label: 'Agentic Pack' },
  { value: 'AGENTIC_GOVERNANCE', label: 'Agentic Protection' },
  { value: 'CONTINUOUS_TRUST',   label: 'Continuous Trust Platform' },
];
const MODULES = [
  { value: 'CORE',             label: 'Control Core' },
  { value: 'WORKFLOW_ORCH',    label: 'Workflow Orchestration' },
  { value: 'WORKFLOW_PROTECT', label: 'Workflow Protection' },
  { value: 'GOVERNANCE',       label: 'Protection Layer' },
  { value: 'AGENT_GOV',        label: 'Agent Protection' },
  { value: 'EXT_AGENT_GOV',    label: 'External Agent Protection' },
  { value: 'SHADOW_DISCOVERY', label: 'Shadow AI Discovery' },
  { value: 'TRUST_COMPLIANCE', label: 'Trust & Compliance Center' },
  { value: 'IDENTITY_AUTHZ',   label: 'Identity & Authorization' },
  { value: 'API_GATEWAY',      label: 'Protected Gateway & API Access' },
  { value: 'PROVE7_CONTAINER', label: 'Prove7 Standalone Container' },
];
const SIZES = [
  { value: 'XS', label: 'XS · 2 vCPU · 4 GB' },
  { value: 'S',  label: 'S · 2 vCPU · 8 GB' },
  { value: 'M',  label: 'M · 4 vCPU · 8 GB' },
  { value: 'LG', label: 'LG · 8 vCPU · 16 GB' },
  { value: 'XL', label: 'XL · 16 vCPU · 32 GB' },
];
const MODES = [
  { value: 'prove7_hosted', label: 'Prove7 Cloud' },
  { value: 'self_hosted',   label: 'Self-Hosted' },
];
const PROVIDERS = [
  { value: 'gcp',   label: 'GCP' },
  { value: 'aws',   label: 'AWS' },
  { value: 'azure', label: 'Azure' },
];
const RESIDENCY = [
  { value: 'region_locked',             label: 'Region-locked' },
  { value: 'backup_export_same_region', label: 'Backup export (same region)' },
  { value: 'cross_region_to_paired',    label: 'Cross-region to paired' },
];
const SUPPORT = [
  { value: 'STANDARD', label: 'Standard' },
  { value: 'PREMIUM',  label: 'Premium' },
  { value: 'PREMIER',  label: 'Premier' },
];
const SLA = [
  { value: 'single_az',                    label: 'Single-AZ' },
  { value: 'multi_az',                     label: 'Multi-AZ' },
  { value: 'multi_region_active_standby',  label: 'Multi-region (active-standby)' },
  { value: 'multi_region_active_active',   label: 'Multi-region (active-active)' },
];
const BILLING = [
  { value: 'PRE_COMMITTED', label: 'Pre-committed' },
  { value: 'USAGE_BASED',   label: 'Usage-based' },
];
const TERMS = [
  { value: 'MONTHLY',   label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'YEARLY',    label: 'Yearly' },
  { value: 'CUSTOM',    label: 'Custom' },
];

const ORDER_TONE = { DRAFT: 'warning', ACTIVE: 'success', CANCELLED: 'muted', SUPERSEDED: 'muted' };
const LINE_TONE  = { DRAFT: 'warning', PROVISIONED: 'action', ACTIVE: 'success', CANCELLED: 'muted' };

// Both /admin/orders helpers resolve the raw apiFetch envelope ({ ok, data }).
const dataOf = (res) => (res && typeof res === 'object' && 'ok' in res ? res.data : res);
const errOf  = (e) => e?.message || (typeof e === 'string' ? e : 'Something went wrong');

const EMPTY_ORDER = {
  orderNumber: '', name: '', term: 'YEARLY', currency: 'USD',
  effectiveFrom: today(), effectiveTo: '',
};
const EMPTY_LINE = {
  editionKey: 'GOVERNED_WORKFLOW', modules: [],
  instanceSize: 'M', deploymentMode: 'prove7_hosted', provider: 'gcp',
  region: 'us-central1', pairedRegion: '',
  dataResidency: 'region_locked', supportType: 'STANDARD',
  slaTier: 'single_az', billingMode: 'PRE_COMMITTED', quantity: 1,
  // Where the deployment provisions. Required for a deployment line — the operator
  // just clicks Deploy in Cloud Operations, so the target tenant is fixed here.
  targetTenantId: '', targetOrgId: '',
};

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function OrderFormsPage() {
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerId, setCustomerId] = useState('');

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [detail, setDetail] = useState(null);          // { order, lines }
  const [detailLoading, setDetailLoading] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [showAddLine, setShowAddLine] = useState(false);
  const [editingOrder, setEditingOrder] = useState(false);
  const [editingLineId, setEditingLineId] = useState(null);
  const [busy, setBusy] = useState(false);
  // The selected customer's tenants (where a deployment line can provision).
  const [tenants, setTenants] = useState([]);

  // Load the customers this Platform Admin may author order forms for.
  useEffect(() => {
    let alive = true;
    setCustomersLoading(true);
    api.listCustomers()
      .then((res) => {
        if (!alive) return;
        const rows = dataOf(res);
        setCustomers(Array.isArray(rows) ? rows : []);
      })
      .catch(() => { if (alive) setCustomers([]); })
      .finally(() => { if (alive) setCustomersLoading(false); });
    return () => { alive = false; };
  }, []);

  const loadOrders = useMemo(() => (cid) => {
    if (!cid) { setOrders([]); return Promise.resolve(); }
    setOrdersLoading(true);
    return api.listOrders(cid)
      .then((res) => {
        const rows = dataOf(res);
        setOrders(Array.isArray(rows) ? rows : []);
      })
      .catch((e) => { setOrders([]); pushGlobalToast({ tone: 'danger', title: 'Could not load order forms', description: errOf(e) }); })
      .finally(() => setOrdersLoading(false));
  }, []);

  useEffect(() => {
    setSelectedOrderId('');
    setDetail(null);
    loadOrders(customerId);
  }, [customerId, loadOrders]);

  // Load the selected customer's tenants (org / tenant) so a deployment line can
  // name where it provisions. PA sees all orgs + tenants; we join locally to the
  // customer (org.customerId) — no bespoke endpoint needed.
  useEffect(() => {
    if (!customerId) { setTenants([]); return undefined; }
    let alive = true;
    Promise.all([api.getOrganizations(), api.getTenants()])
      .then(([orgRes, tenRes]) => {
        if (!alive) return;
        const orgs = dataOf(orgRes); const tens = dataOf(tenRes);
        const orgById = new Map((Array.isArray(orgs) ? orgs : []).map((o) => [o.id, o]));
        const mine = (Array.isArray(tens) ? tens : [])
          .filter((t) => { const o = orgById.get(t.orgId); return o && o.customerId === customerId; })
          .map((t) => ({ id: t.id, orgId: t.orgId, label: `${orgById.get(t.orgId)?.name || 'Org'} / ${t.name}` }));
        setTenants(mine);
      })
      .catch(() => { if (alive) setTenants([]); });
    return () => { alive = false; };
  }, [customerId]);

  const loadDetail = (orderId) => {
    if (!orderId) { setDetail(null); return; }
    setDetailLoading(true);
    api.getOrder(orderId)
      .then((res) => setDetail(dataOf(res) || null))
      .catch((e) => { setDetail(null); pushGlobalToast({ tone: 'danger', title: 'Could not load order form', description: errOf(e) }); })
      .finally(() => setDetailLoading(false));
  };

  const selectOrder = (orderId) => {
    setSelectedOrderId(orderId);
    setShowAddLine(false);
    setEditingOrder(false);
    setEditingLineId(null);
    loadDetail(orderId);
  };

  const onCreateOrder = async (formVals) => {
    setBusy(true);
    try {
      const res = await api.createOrder({ customerId, ...normalizeOrder(formVals) });
      const created = dataOf(res);
      pushGlobalToast({ tone: 'success', title: 'Order form created', description: `${created?.orderNumber || 'Draft'} is ready for deployment lines.` });
      setShowCreate(false);
      await loadOrders(customerId);
      if (created?.id) selectOrder(created.id);
    } catch (e) {
      pushGlobalToast({ tone: 'danger', title: 'Could not create order form', description: errOf(e) });
    } finally { setBusy(false); }
  };

  const onAddLine = async (lineVals) => {
    if (!selectedOrderId) return;
    setBusy(true);
    try {
      await api.addOrderLine(selectedOrderId, normalizeLine(lineVals));
      pushGlobalToast({ tone: 'success', title: 'Deployment line added', description: 'It will be provisionable once the order form is active.' });
      setShowAddLine(false);
      loadDetail(selectedOrderId);
    } catch (e) {
      pushGlobalToast({ tone: 'danger', title: 'Could not add deployment line', description: errOf(e) });
    } finally { setBusy(false); }
  };

  const onUpdateOrder = async (formVals) => {
    if (!selectedOrderId) return;
    setBusy(true);
    try {
      await api.updateOrder(selectedOrderId, normalizeOrder(formVals));
      pushGlobalToast({ tone: 'success', title: 'Order form updated' });
      setEditingOrder(false);
      await loadOrders(customerId);
      loadDetail(selectedOrderId);
    } catch (e) {
      pushGlobalToast({ tone: 'danger', title: 'Could not update order form', description: errOf(e) });
    } finally { setBusy(false); }
  };

  const onUpdateLine = async (lineId, lineVals) => {
    setBusy(true);
    try {
      await api.updateOrderLine(lineId, normalizeLine(lineVals));
      pushGlobalToast({ tone: 'success', title: 'Deployment line updated' });
      setEditingLineId(null);
      loadDetail(selectedOrderId);
    } catch (e) {
      pushGlobalToast({ tone: 'danger', title: 'Could not update deployment line', description: errOf(e) });
    } finally { setBusy(false); }
  };

  const onDeleteLine = async (lineId) => {
    if (!window.confirm('Remove this deployment line from the order form? This cannot be undone.')) return;
    setBusy(true);
    try {
      await api.deleteOrderLine(lineId);
      pushGlobalToast({ tone: 'success', title: 'Deployment line removed' });
      if (editingLineId === lineId) setEditingLineId(null);
      loadDetail(selectedOrderId);
    } catch (e) {
      pushGlobalToast({ tone: 'danger', title: 'Could not remove deployment line', description: errOf(e) });
    } finally { setBusy(false); }
  };

  const onDeleteOrder = async () => {
    if (!detail?.order?.id) return;
    if (!window.confirm('Delete this draft order form and all its lines? This cannot be undone.')) return;
    setBusy(true);
    try {
      await api.deleteOrder(detail.order.id);
      pushGlobalToast({ tone: 'success', title: 'Order form deleted' });
      setSelectedOrderId('');
      setDetail(null);
      loadOrders(customerId);
    } catch (e) {
      pushGlobalToast({ tone: 'danger', title: 'Could not delete order form', description: errOf(e) });
    } finally { setBusy(false); }
  };

  const onActivate = async () => {
    if (!detail?.order?.id) return;
    setBusy(true);
    try {
      await api.updateOrder(detail.order.id, { status: 'ACTIVE' });
      pushGlobalToast({ tone: 'success', title: 'Order form activated', description: 'Its deployment lines are now provisionable in Cloud Operations.' });
      await loadOrders(customerId);
      loadDetail(detail.order.id);
    } catch (e) {
      pushGlobalToast({ tone: 'danger', title: 'Could not activate order form', description: errOf(e) });
    } finally { setBusy(false); }
  };

  return (
    <V2PageShell
      title="Order Forms"
      subtitle="Author the signed order forms that entitle a customer's deployments. New order forms add incremental deployments — existing ones are never disturbed."
    >
      <div className="mb-4 max-w-md">
        <Label>Customer</Label>
        <CustomerPicker
          customers={customers}
          value={customerId}
          onChange={(id) => setCustomerId(id || '')}
          loading={customersLoading}
          ariaLabel="Select a customer"
        />
      </div>

      {!customerId ? (
        <EmptyState
          icon={<Building2 size={20} />}
          title="Choose a customer"
          description="Order forms are authored per customer. Pick one to see their order forms or create the first."
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,360px)_1fr] gap-4 items-start">
          {/* LEFT — order-form list */}
          <Card padding="md">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[13px] font-semibold text-v2-ink">Order forms</div>
              <Button variant="secondary" size="sm" iconLeft={<Plus size={14} />} onClick={() => setShowCreate(true)}>
                New
              </Button>
            </div>

            {showCreate && (
              <OrderForm busy={busy} onCancel={() => setShowCreate(false)} onSubmit={onCreateOrder} />
            )}

            {ordersLoading ? (
              <div className="text-[12px] text-v2-ink-muted py-4">Loading order forms…</div>
            ) : orders.length === 0 ? (
              <div className="text-[12px] text-v2-ink-muted py-4">
                No order forms yet. Create the first to add deployment lines.
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {orders.map((o) => (
                  <li key={o.id}>
                    <button
                      type="button"
                      onClick={() => selectOrder(o.id)}
                      className={`w-full text-left rounded-v2-md border px-3 py-2 transition-colors ${
                        selectedOrderId === o.id
                          ? 'border-v2-action bg-v2-action-soft'
                          : 'border-v2-border bg-v2-surface hover:border-v2-border-strong'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[13px] font-semibold text-v2-ink truncate">
                          {o.orderNumber || 'Untitled'}
                        </span>
                        <Pill tone={ORDER_TONE[o.status] || 'muted'} size="sm">{o.status}</Pill>
                      </div>
                      {o.name && <div className="text-[12px] text-v2-ink-muted truncate mt-0.5">{o.name}</div>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* RIGHT — selected order detail + lines */}
          <div>
            {!selectedOrderId ? (
              <EmptyState
                icon={<FileText size={20} />}
                title="Select an order form"
                description="Pick an order form on the left to review its deployment lines, or create a new one."
              />
            ) : detailLoading ? (
              <Card padding="md"><div className="text-[12px] text-v2-ink-muted">Loading order form…</div></Card>
            ) : detail?.order ? (
              <OrderDetail
                detail={detail}
                busy={busy}
                tenants={tenants}
                onActivate={onActivate}
                onDeleteOrder={onDeleteOrder}
                editingOrder={editingOrder}
                onEditOrderClick={() => setEditingOrder(true)}
                onEditOrderCancel={() => setEditingOrder(false)}
                onEditOrderSubmit={onUpdateOrder}
                showAddLine={showAddLine}
                onAddLineClick={() => setShowAddLine(true)}
                onAddLineCancel={() => setShowAddLine(false)}
                onAddLineSubmit={onAddLine}
                editingLineId={editingLineId}
                onEditLineClick={setEditingLineId}
                onEditLineCancel={() => setEditingLineId(null)}
                onEditLineSubmit={onUpdateLine}
                onDeleteLine={onDeleteLine}
              />
            ) : null}
          </div>
        </div>
      )}
    </V2PageShell>
  );
}

// ── Order detail + lines ────────────────────────────────────────────
function OrderDetail({
  detail, busy, tenants, onActivate, onDeleteOrder,
  editingOrder, onEditOrderClick, onEditOrderCancel, onEditOrderSubmit,
  showAddLine, onAddLineClick, onAddLineCancel, onAddLineSubmit,
  editingLineId, onEditLineClick, onEditLineCancel, onEditLineSubmit, onDeleteLine,
}) {
  const { order, lines = [] } = detail;
  const isDraft = order.status === 'DRAFT';
  return (
    <Card padding="md">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-semibold text-v2-ink">{order.orderNumber || 'Untitled order form'}</span>
            <Pill tone={ORDER_TONE[order.status] || 'muted'} size="sm">{order.status}</Pill>
          </div>
          {order.name && <div className="text-[12px] text-v2-ink-muted mt-0.5">{order.name}</div>}
          <div className="text-[11px] text-v2-ink-muted mt-1">
            {[order.term, order.currency,
              order.effectiveFrom ? `from ${order.effectiveFrom}` : null,
              order.effectiveTo ? `to ${order.effectiveTo}` : null,
            ].filter(Boolean).join(' · ')}
          </div>
        </div>
        {isDraft && !editingOrder && (
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" size="sm" onClick={onEditOrderClick} disabled={busy}>Edit</Button>
            <Button variant="ghost" size="sm" onClick={onDeleteOrder} disabled={busy}>Delete</Button>
            <Button variant="primary" size="sm" loading={busy} onClick={onActivate}>Activate</Button>
          </div>
        )}
      </div>

      {isDraft && editingOrder && (
        <div className="mb-3">
          <OrderForm
            busy={busy}
            initial={orderToForm(order)}
            submitLabel="Save changes"
            onCancel={onEditOrderCancel}
            onSubmit={onEditOrderSubmit}
          />
        </div>
      )}

      {isDraft ? (
        <div className="text-[11px] text-v2-ink-muted mb-3 rounded-v2-md bg-v2-muted px-3 py-2">
          Draft order forms are not yet provisionable. Add or edit the deployment lines, then activate the form
          to make them available in Cloud Operations.
        </div>
      ) : (
        <div className="text-[11px] text-v2-ink-muted mb-3 rounded-v2-md bg-v2-muted px-3 py-2">
          This order form is {String(order.status).toLowerCase()} and locked. To add or change deployments, create a
          new order form — it adds incrementally and leaves existing deployments untouched.
        </div>
      )}

      <div className="flex items-center justify-between mb-2">
        <div className="text-[13px] font-semibold text-v2-ink">Deployment &amp; service lines</div>
        {isDraft && (
          <Button variant="secondary" size="sm" iconLeft={<Plus size={14} />} onClick={onAddLineClick}>
            Add deployment line
          </Button>
        )}
      </div>

      {isDraft && showAddLine && (
        <div className="mb-3">
          <LineForm busy={busy} tenants={tenants} onCancel={onAddLineCancel} onSubmit={onAddLineSubmit} />
        </div>
      )}

      {lines.length === 0 ? (
        <div className="text-[12px] text-v2-ink-muted py-3">No lines on this order form yet.</div>
      ) : (
        <ul className="flex flex-col gap-2">
          {lines.map((l) => {
            // Only a DRAFT line on a DRAFT order form may be edited or removed; a provisioned
            // line is live infrastructure and never editable here.
            const lineEditable = isDraft && l.status === 'DRAFT';
            return editingLineId === l.id ? (
              <li key={l.id}>
                <LineForm
                  busy={busy}
                  tenants={tenants}
                  initial={lineToForm(l)}
                  submitLabel="Save changes"
                  onCancel={onEditLineCancel}
                  onSubmit={(vals) => onEditLineSubmit(l.id, vals)}
                />
              </li>
            ) : (
              <LineRow
                key={l.id}
                line={l}
                editable={lineEditable}
                busy={busy}
                onEdit={() => onEditLineClick(l.id)}
                onDelete={() => onDeleteLine(l.id)}
              />
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function LineRow({ line, editable, busy, onEdit, onDelete }) {
  const isDeployment = !!(line.instanceSize && line.region);
  const spec = [
    line.editionKey,
    (line.modules && line.modules.length) ? `${line.modules.length} module${line.modules.length > 1 ? 's' : ''}` : null,
    isDeployment ? [line.provider, line.region].filter(Boolean).join('/') : null,
    line.instanceSize || null,
    line.slaTier || null,
  ].filter(Boolean).join(' · ');
  return (
    <li className="rounded-v2-md border border-v2-border bg-v2-surface px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {isDeployment && <Rocket size={13} className="text-v2-ink-muted shrink-0" />}
          <span className="text-[13px] font-semibold text-v2-ink truncate">
            {line.lineNo != null ? `#${line.lineNo} · ` : ''}{line.editionKey || 'Line'}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {editable && (
            <>
              <button type="button" onClick={onEdit} disabled={busy}
                className="text-[12px] text-v2-action hover:underline disabled:opacity-50">Edit</button>
              <button type="button" onClick={onDelete} disabled={busy}
                className="text-[12px] text-v2-ink-muted hover:underline disabled:opacity-50">Remove</button>
            </>
          )}
          <Pill tone={LINE_TONE[line.status] || 'muted'} size="sm">{line.status}</Pill>
        </div>
      </div>
      <div className="text-[12px] text-v2-ink-muted truncate mt-0.5">{spec}</div>
    </li>
  );
}

// ── Create / edit order-form header ─────────────────────────────────
function OrderForm({ busy, initial, onCancel, onSubmit, submitLabel = 'Create order form' }) {
  const [v, setV] = useState(initial || EMPTY_ORDER);
  const set = (patch) => setV((p) => ({ ...p, ...patch }));
  const canSubmit = v.orderNumber.trim().length > 0 && !busy;
  return (
    <div className="mb-3 rounded-v2-md border border-v2-border bg-v2-muted p-3">
      <div className="grid grid-cols-2 gap-2">
        <FieldGroup label="Order form no." required>
          <Input size="sm" value={v.orderNumber} placeholder="OF-2026-001"
            onChange={(e) => set({ orderNumber: e.target.value })} />
        </FieldGroup>
        <FieldGroup label="Name">
          <Input size="sm" value={v.name} placeholder="Initial subscription"
            onChange={(e) => set({ name: e.target.value })} />
        </FieldGroup>
        <FieldGroup label="Term">
          <Select size="sm" value={v.term} onChange={(e) => set({ term: e.target.value })}>
            {TERMS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Select>
        </FieldGroup>
        <FieldGroup label="Currency">
          <Input size="sm" value={v.currency} maxLength={3} placeholder="USD"
            onChange={(e) => set({ currency: e.target.value.toUpperCase() })} />
        </FieldGroup>
        <FieldGroup label="Effective from">
          <Input size="sm" type="date" value={v.effectiveFrom}
            onChange={(e) => set({ effectiveFrom: e.target.value })} />
        </FieldGroup>
        <FieldGroup label="Effective to" hint="Leave blank for open-ended.">
          <Input size="sm" type="date" value={v.effectiveTo}
            onChange={(e) => set({ effectiveTo: e.target.value })} />
        </FieldGroup>
      </div>
      <div className="flex justify-end gap-2 mt-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="primary" size="sm" loading={busy} disabled={!canSubmit}
          onClick={() => onSubmit(v)}>{submitLabel}</Button>
      </div>
    </div>
  );
}

// ── Add / edit deployment-line form ─────────────────────────────────
function LineForm({ busy, tenants = [], initial, onCancel, onSubmit, submitLabel = 'Add deployment line' }) {
  const [v, setV] = useState(initial || EMPTY_LINE);
  const set = (patch) => setV((p) => ({ ...p, ...patch }));
  const toggleModule = (m) => setV((p) => ({
    ...p,
    modules: p.modules.includes(m) ? p.modules.filter((x) => x !== m) : [...p.modules, m],
  }));
  const selectTenant = (tenantId) => {
    const t = tenants.find((x) => x.id === tenantId);
    set({ targetTenantId: tenantId, targetOrgId: t ? t.orgId : '' });
  };
  const isMultiRegion = v.slaTier.startsWith('multi_region');
  const canSubmit = !!(v.editionKey && v.provider && v.region.trim() && v.instanceSize && v.targetTenantId) && !busy;
  return (
    <div className="rounded-v2-md border border-v2-border bg-v2-muted p-3">
      <div className="grid grid-cols-2 gap-2">
        <FieldGroup label="Edition" required>
          <Select size="sm" value={v.editionKey} onChange={(e) => set({ editionKey: e.target.value })}>
            {EDITIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </FieldGroup>
        <FieldGroup label="Deployment mode" required>
          <Select size="sm" value={v.deploymentMode} onChange={(e) => set({ deploymentMode: e.target.value })}>
            {MODES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </FieldGroup>
        <FieldGroup label="Cloud provider" required>
          <Select size="sm" value={v.provider} onChange={(e) => set({ provider: e.target.value })}>
            {PROVIDERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </FieldGroup>
        <FieldGroup label="Instance size" required>
          <Select size="sm" value={v.instanceSize} onChange={(e) => set({ instanceSize: e.target.value })}>
            {SIZES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </FieldGroup>
        <FieldGroup label="Region" required>
          <Input size="sm" value={v.region} placeholder="us-central1"
            onChange={(e) => set({ region: e.target.value })} />
        </FieldGroup>
        <FieldGroup label="Paired region" hint={isMultiRegion ? 'Required for multi-region SLA.' : 'Optional.'}>
          <Input size="sm" value={v.pairedRegion} placeholder="us-east4"
            onChange={(e) => set({ pairedRegion: e.target.value })} />
        </FieldGroup>
        <FieldGroup label="SLA tier">
          <Select size="sm" value={v.slaTier} onChange={(e) => set({ slaTier: e.target.value })}>
            {SLA.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </FieldGroup>
        <FieldGroup label="Data residency">
          <Select size="sm" value={v.dataResidency} onChange={(e) => set({ dataResidency: e.target.value })}>
            {RESIDENCY.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </FieldGroup>
        <FieldGroup label="Support tier">
          <Select size="sm" value={v.supportType} onChange={(e) => set({ supportType: e.target.value })}>
            {SUPPORT.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </FieldGroup>
        <FieldGroup label="Billing mode">
          <Select size="sm" value={v.billingMode} onChange={(e) => set({ billingMode: e.target.value })}>
            {BILLING.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </FieldGroup>
        <FieldGroup label="Target tenant" required
          hint={tenants.length ? 'Where this deployment provisions.' : 'No tenants found for this customer.'}>
          <Select size="sm" value={v.targetTenantId} disabled={!tenants.length}
            onChange={(e) => selectTenant(e.target.value)}>
            <option value="">{tenants.length ? 'Choose a tenant…' : 'No tenants available'}</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </Select>
        </FieldGroup>
      </div>

      <div className="mt-2">
        <Label>Modules <span className="font-normal text-v2-ink-muted">(optional — edition already bundles its modules)</span></Label>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {MODULES.map((m) => {
            const on = v.modules.includes(m.value);
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => toggleModule(m.value)}
                className={`text-[11px] rounded-v2-pill border px-2.5 py-1 transition-colors ${
                  on ? 'border-v2-action bg-v2-action-soft text-v2-action font-semibold'
                     : 'border-v2-border bg-v2-surface text-v2-ink-muted hover:border-v2-border-strong'
                }`}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-3">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="primary" size="sm" loading={busy} disabled={!canSubmit}
          onClick={() => onSubmit(v)}>{submitLabel}</Button>
      </div>
    </div>
  );
}

// Map a persisted line (camelCase from the API) back onto the editable form shape.
function lineToForm(l) {
  return {
    editionKey: l.editionKey || 'GOVERNED_WORKFLOW',
    modules: Array.isArray(l.modules) ? l.modules : [],
    instanceSize: l.instanceSize || 'M',
    deploymentMode: l.deploymentMode || 'prove7_hosted',
    provider: l.provider || 'gcp',
    region: l.region || '',
    pairedRegion: l.pairedRegion || '',
    dataResidency: l.dataResidency || 'region_locked',
    supportType: l.supportType || 'STANDARD',
    slaTier: l.slaTier || 'single_az',
    billingMode: l.billingMode || 'PRE_COMMITTED',
    quantity: l.quantity || 1,
    targetTenantId: l.targetTenantId || '',
    targetOrgId: l.targetOrgId || '',
  };
}

// Map a persisted order header onto the editable form shape.
function orderToForm(o) {
  return {
    orderNumber: o.orderNumber || '',
    name: o.name || '',
    term: o.term || 'YEARLY',
    currency: o.currency || 'USD',
    effectiveFrom: o.effectiveFrom || '',
    effectiveTo: o.effectiveTo || '',
  };
}

// ── Payload normalizers — strip empties so the server applies its own defaults
//    and the optional date/field vocab validators only see real values.
function normalizeOrder(v) {
  return {
    orderNumber: v.orderNumber.trim(),
    name: v.name.trim() || null,
    term: v.term || null,
    currency: v.currency.trim() || null,
    effectiveFrom: v.effectiveFrom || null,
    effectiveTo: v.effectiveTo || null,
  };
}

function normalizeLine(v) {
  return {
    editionKey: v.editionKey || null,
    modules: v.modules.length ? v.modules : null,
    instanceSize: v.instanceSize || null,
    deploymentMode: v.deploymentMode || null,
    provider: v.provider || null,
    region: v.region.trim() || null,
    pairedRegion: v.pairedRegion.trim() || null,
    dataResidency: v.dataResidency || null,
    supportType: v.supportType || null,
    slaTier: v.slaTier || null,
    billingMode: v.billingMode || null,
    quantity: v.quantity && v.quantity > 0 ? Number(v.quantity) : 1,
    targetTenantId: v.targetTenantId || null,
    targetOrgId: v.targetOrgId || null,
  };
}
